import { Line, mixins } from "vue-chartjs";
import moment from "moment";
import { createNamespacedHelpers } from "vuex";
import { formatNumberWithComma } from "@/utils";
import store from "@/store";

const { mapGetters: userGetters } = createNamespacedHelpers("user"),
	{ mapGetters: filtersGetters } = createNamespacedHelpers("filters"),
	{ mapGetters: brandingGetters } = createNamespacedHelpers("branding");

export default {
	name: "PeakGraph",
	extends: Line,
	props: {
		activeSort: {
			// true - currency, false - weight
			type: Boolean,
			default: false,
		},
		graphData: {
			type: [Array, Object],
			required: true,
		},
	},
	mixins: [mixins.reactiveData],
	data() {
		return {
			name: "PeakGraph",
			comparePeriod: "day_before",
			graphLoaded: false,
			activeWasteBin: 0,
			datasets: [],
			datasetColors: [],
			activeLabelSlotIndex: 0,
			data: {
				labels: [],
				datasets: [],
			},
			options: {
				tooltips: {
					callbacks: {
						title() {
							// don't show date in the tooltip when hovering in the graph
							return "";
						},
					},
				},
				legend: { display: false },
				layout: { padding: { left: 50 } },
				responsive: true,
				maintainAspectRatio: false,
				animation: {
					easing: "easeInOutQuad",
					duration: 520,
				},
				annotation: { annotations: [] },
				scales: {
					xAxes: [
						{
							display: true,
							gridLines: {
								display: false,
								color: "rgba(200, 200, 200, 0)",
								lineWidth: 1,
								drawBorder: false,
								padding: "20px",
							},
							ticks: {
								fontSize: 11,
								fontColor: store.getters["branding/colors"][0],
								padding: 10,
							},
						},
					],
					yAxes: [
						{
							gridLines: {
								color: "rgba(200, 200, 200, 0.3)",
								lineWidth: 1,
								drawBorder: false,
							},
							ticks: {
								beginAtZero: true,
								stepSize: 0.1,
								maxTicksLimit: 10,
								padding: 10,
								fontSize: 11,
								fontColor: store.getters["branding/colors"][0],
								callback: (value) => this.getChartYAxisLabel(value),
							},
						},
					],
				},
			},
		};
	},

	computed: {
		...userGetters(["activeOutlet", "weightUnit", "weightFactor"]),
		...brandingGetters(["colors"]),
		...filtersGetters(["activePeriod", "activeCompareTo", "activeDate"]),
	},

	methods: {
		parseDataAndInitDataset,
		initGraph,
		init,
		setComparePeriod,
		populateLabels,
		labelSlots,
		getChartYAxisLabel,
	},
	watch: {
		activeSort: {
			handler: function () {
				this.init();
			},
			deep: true,
		},
	},
	mounted() {
		this.init();
	},
};

function labelSlots() {
	return {
		week: [
			[
				this.$t("days.monday"),
				this.$t("days.tuesday"),
				this.$t("days.wednesday"),
				this.$t("days.thursday"),
				this.$t("days.friday"),
				this.$t("days.saturday"),
				this.$t("days.sunday"),
			],
		],
		day: [
			[
				"03:30",
				"04:30",
				"05:30",
				"06:30",
				"07:30",
				"08:30",
				"09:30",
				"10:30",
				"11:30",
				"12:30",
				"13:30",
				"14:30",
				"15:30",
				"16:30",
				"17:30",
				"18:30",
				"19:30",
				"20:30",
				"21:30",
				"22:30",
				"23:30",
				"00:30",
				"01:30",
				"02:30",
			],
		],
		month: [Array.from({ length: moment(this.activeDate).daysInMonth() }, (val, key) => key + 1)],
		year: [
			[
				this.$t("month.january"),
				this.$t("month.february"),
				this.$t("month.march"),
				this.$t("month.april"),
				this.$t("month.may"),
				this.$t("month.june"),
				this.$t("month.july"),
				this.$t("month.august"),
				this.$t("month.september"),
				this.$t("month.october"),
				this.$t("month.november"),
				this.$t("month.december"),
			],
		],
	};
}

function setComparePeriod() {
	if (this.activePeriod === "month" || this.activeCompareTo === "base_month")
		this.comparePeriod = "first_month_measured";
	else this.comparePeriod = this.activeCompareTo;
}

function initGraph() {
	this.graphData.forEach((element, index) => {
		this.parseDataAndInitDataset(element, index);
	});

	this.renderChart(this.data, this.options);
}

// eslint-disable-next-line complexity
function parseDataAndInitDataset(data, index) {
	((this.datasetColors = [this.colors[3], "#7D7D7D"]), (this.data.datasets = []));
	this.$forceUpdate();

	const kgModifier = 1000,
		DECIMALS = 2;
	let parsedDataForGraph = new Array(this.data.labels.length).fill(null),
		propName;

	switch (this.activePeriod) {
		case "day":
			propName = "hour";
			break;
		case "week":
		case "month":
			propName = "day";
			break;
		case "year":
			propName = "month";
			break;
	}

	Object.keys(data).forEach((key) => {
		const index =
			this.activePeriod === "day"
				? data[key][propName] // positional for 'day': hour=0(3:30), hour=1(4:30), ...
				: data[key][propName] - 1;

		parsedDataForGraph[index] = this.activeSort
			? Math.round(data[key].cost)
			: (data[key].weight * this.weightFactor) / kgModifier;
	});

	// round graph numbers to 2 decimals
	parsedDataForGraph = parsedDataForGraph.map((value) => {
		if (typeof value === "number") return value.toFixed(DECIMALS);

		return value;
	});

	this.datasets.push({
		lineTension: 0,
		label: this.activeSort ? this.$t("global.cost") : this.$t("global.weight"),
		data: parsedDataForGraph,
		fullData: parsedDataForGraph,
		backgroundColor: "rgba(0, 0, 0, 0.0)",
		borderColor: this.datasetColors[index],
		// eslint-disable-next-line no-magic-numbers
		borderDash: index === 1 ? [10, 10] : [],
		borderWidth: 2,
		pointBackgroundColor: this.datasetColors[index],
		pointRadius: 4,
		active: true,
		mainColor: this.datasetColors[index],
	});

	for (let index = 0; index < this.datasets.length; index++) this.data.datasets.push(this.datasets[index]);
}

// eslint-disable-next-line complexity
function populateLabels() {
	const startDate = this.activeDate;
	this.data.labels = [];
	this.data.labels = this.labelSlots()[this.activePeriod][this.activeLabelSlotIndex];

	if (this.activePeriod === "day") {
		const date = moment(startDate).startOf(this.activePeriod);

		if (this.$utils.isClosed(date)) this.data.labels = [this.$t("global.closed")];

		return;
	}

	for (let index = 0; index < this.data.labels.length; index++) {
		if (this.activePeriod === "week") {
			const fullDayName = this.data.labels[index],
				date = moment(startDate).startOf("isoweek").add(index, "days");

			let label = "";

			if (this.$utils.isClosed(date)) label = this.$t("global.closed");
			else label = [fullDayName, date.format("DD.MM.YYYY")];

			this.data.labels[index] = label;
		} else if (this.activePeriod === "month") {
			let shortDayName = moment(startDate).startOf(this.activePeriod).add(index, "days").format("dd."),
				label = "";

			const date = moment(startDate).startOf(this.activePeriod).add(index, "days");

			shortDayName = this.$utils.replaceDoubleDotWithSingleDot(shortDayName);

			if (this.$utils.isClosed(date)) label = this.$t("global.closed");
			else label = `${shortDayName} ${date.format("DD/MM/YYYY")}`;

			this.data.labels[index] = label;
		} else if (this.activePeriod === "year")
			this.data.labels[index] = this.data.labels[index] + " " + moment(this.activeDate).year();
	}
}

function init() {
	this.data.datasets = [];
	this.datasets = [];

	if (!this.activePeriod) return;

	if (!this.graphLoaded) this.populateLabels();

	this.setComparePeriod();
	this.initGraph(this.graphData);
	this.graphLoaded = true;
}

function getChartYAxisLabel(value) {
	let maxValue = 0;
	for (let index = 0; index < this.data.datasets.length; index++) {
		const datasetMax = Math.max(...this.data.datasets[index].data);
		if (datasetMax > maxValue) maxValue = datasetMax;
	}
	const decimalPlaces = maxValue < 5 && maxValue > 0 ? 1 : 0;

	if (this.activeSort) return `${this.activeOutlet.currency} ${formatNumberWithComma(value, decimalPlaces)}`;

	return `${formatNumberWithComma(value, decimalPlaces)} ${this.weightUnit}`;
}
